import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import { useAuth } from "@clerk/clerk-react";
import { globalEventSchema } from "./useGlobalEvents";

const EventType = z.enum(["LEVEL_UP", "POINT_REWARD", "NEW_CONTENT", "NOTIFICATION"]);

const eventSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    type: EventType,
    userId: z.string(),
    link: z.string().nullable().optional(),
    read: z.boolean(),
    expRewardAmount: z.number().optional().nullable(),

    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export type Event = z.infer<typeof eventSchema>;

export const getEvents = (options?: any) => {
    const { getToken, isSignedIn } = useAuth();
    return useQuery({
        queryKey: ["events"],
        queryFn: async () => {
            const token = await getToken();
            // If there's no token yet, return an empty list without calling the API
            if (!token) {
                return [] as z.infer<typeof eventSchema>[];
            }
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/events`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return z.array(eventSchema).parse(response.data);
        },
        refetchInterval: 30000,
        // Only run when signed in; still allow caller to override via options
        enabled: isSignedIn,
        ...options,
    });
};

export const useReadEvent = () => {
    const queryClient = useQueryClient();
    const { getToken } = useAuth();
    return useMutation({
        mutationFn: async (eventId: string) => {
            const token = await getToken();
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/app/events/${eventId}/mark-read`,
                {}, // empty body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return eventSchema.parse(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["events"] });
            queryClient.invalidateQueries({ queryKey: ["mixedEvents"] });
            queryClient.invalidateQueries({ queryKey: ["user", "level"] });
            queryClient.invalidateQueries({ queryKey: ["user"] });
        },
    });
};

export const readAllEvents = () => {
    const queryClient = useQueryClient();
    const { getToken } = useAuth();
    return useMutation({
        mutationFn: async () => {
            const token = await getToken();
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/app/events/mark-all-read`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return z.object({ success: z.boolean() }).parse(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["events"] });
            queryClient.invalidateQueries({ queryKey: ["mixedEvents"] });
            queryClient.invalidateQueries({ queryKey: ["user", "level"] });
            queryClient.invalidateQueries({ queryKey: ["user"] });
        },
    });
};

const mixedEventSchema = z.object({
    userEvents: z.array(eventSchema),
    globalEvents: z.array(globalEventSchema),
});

export type MixedEventsPayload = z.infer<typeof mixedEventSchema>;

export const getMixedEvents = (options?: any) => {
    const { getToken, isSignedIn } = useAuth();
    return useQuery({
        queryKey: ["mixedEvents"],
        queryFn: async () => {
            try {
                const token = await getToken();
                // If there's no token yet, avoid making the request and return initial empty data
                if (!token) {
                    return { userEvents: [], globalEvents: [] } as MixedEventsPayload;
                }
                const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/events/mixed`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                return mixedEventSchema.parse(response.data);
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        initialData: { userEvents: [], globalEvents: [] } as MixedEventsPayload,
        refetchInterval: 30000,
        keepPreviousData: true,
        retry: false,
        // Only run when signed in; callers can override via options
        enabled: isSignedIn,
        ...options,
    });
};
